import { useEffect, useState, useMemo, useRef } from 'react';
import { motion } from 'framer-motion';
import { fetchLobbyTables } from '../lib/api';
import { LobbyTableSummary, TableType } from '../shared/types';
import { LoadingState, ErrorState, EmptyState } from '../components/StateViews';
import { hapticImpact } from '../lib/telegram';
import { useAuthStore } from '../store/useAuthStore';
import { getSocket } from '../lib/socket';
import { ChipIcon } from '../components/ChipIcon';

type BlindFilter = 'all' | 'low' | 'mid' | 'high';

interface LobbyProps {
  onOpenTable: (table: LobbyTableSummary) => void;
  onOpenProfile: () => void;
}

export function Lobby({ onOpenTable, onOpenProfile }: LobbyProps) {
  const { token, profile } = useAuthStore();
  const [tables, setTables] = useState<LobbyTableSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [typeFilter, setTypeFilter] = useState<'all' | TableType>('all');
  const [blindFilter, setBlindFilter] = useState<BlindFilter>('all');
  const [quickLoading, setQuickLoading] = useState(false);
  const filterScrollRef = useRef<HTMLDivElement>(null);

  // Telegram Desktop / браузер на десктопе крутят колесо только вертикально — горизонтальный
  // блок фильтров это никак не ловит и выглядит "обрезанным". Перенаправляем вертикальный
  // скролл в горизонтальный именно для этого блока (стандартный паттерн для чипов/табов).
  // preventDefault нужен настоящий (не React synthetic-passive), поэтому вешаем нативно.
  useEffect(() => {
    const el = filterScrollRef.current;
    if (!el) return;
    function onWheel(e: WheelEvent) {
      if (Math.abs(e.deltaY) <= Math.abs(e.deltaX)) return; // уже горизонтальный жест (трекпад) — не мешаем
      if (el!.scrollWidth <= el!.clientWidth) return; // скроллить нечего
      e.preventDefault();
      el!.scrollLeft += e.deltaY;
    }
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, []);

  async function load() {
    if (!token) return;
    setError(null);
    try {
      const { tables } = await fetchLobbyTables(token);
      setTables(tables);
    } catch (e: any) {
      setError(e.message ?? 'Не удалось загрузить столы');
    }
  }

  useEffect(() => {
    load();
    const id = setInterval(load, 5000); // обновляем количество игроков раз в 5с
    return () => clearInterval(id);
  }, [token]);

  const filtered = useMemo(() => {
    if (!tables) return [];
    return tables.filter((t) => {
      if (typeFilter !== 'all' && t.type !== typeFilter) return false;
      if (blindFilter === 'low' && t.bigBlind > 20) return false;
      if (blindFilter === 'mid' && (t.bigBlind < 25 || t.bigBlind > 99)) return false;
      if (blindFilter === 'high' && t.bigBlind < 100) return false;
      return true;
    });
  }, [tables, typeFilter, blindFilter]);

  function quickPlay(type: TableType) {
    if (!token) return;
    hapticImpact('medium');
    setQuickLoading(true);
    const socket = getSocket(token);
    socket.emit('table:quickJoin', { type }, (res: { ok: boolean; tableId?: string; error?: string }) => {
      setQuickLoading(false);
      if (res.ok && res.tableId) {
        const table = tables?.find((t) => t.id === res.tableId);
        if (table) onOpenTable(table);
      } else {
        setError(res.error ?? 'Не удалось найти стол');
      }
    });
  }

  return (
    <div className="min-h-screen pb-10">
      <header className="flex items-center justify-between px-4 pt-4 pb-3">
        <div>
          <div className="font-display text-lg text-gold-gradient font-bold">Texas Hold'em</div>
          <div className="text-white/40 text-xs">Виртуальные фишки · ОБМОРОК Studio</div>
        </div>
        <button
          onClick={onOpenProfile}
          className="flex items-center gap-2 bg-ink-800/80 rounded-full pl-1 pr-3 py-1 border border-white/10"
        >
          <div className="w-7 h-7 rounded-full bg-emerald-600 flex items-center justify-center text-xs font-bold">
            {profile?.username?.slice(0, 1).toUpperCase() ?? '?'}
          </div>
          <span className="text-gold-400 text-xs font-semibold tabular-nums flex items-center gap-1">
            <ChipIcon size={13} />
            {profile?.chips?.toLocaleString('ru-RU') ?? 0}
          </span>
        </button>
      </header>

      {/* Быстрый стол — один клик */}
      <div className="px-4 grid grid-cols-2 gap-3 mb-5">
        <motion.button
          whileTap={{ scale: 0.97 }}
          disabled={quickLoading}
          onClick={() => quickPlay('sixmax')}
          style={{
            backgroundImage: `linear-gradient(135deg, rgba(16,150,90,0.75), rgba(6,36,22,0.9)), url(/textures/velvet.jpg)`,
          }}
          className="rounded-2xl p-4 bg-cover bg-center border border-emerald-400/30 text-left shadow-card"
        >
          <div className="text-white font-bold">Быстрый стол</div>
          <div className="text-emerald-100/70 text-xs">6-max · один клик</div>
        </motion.button>
        <motion.button
          whileTap={{ scale: 0.97 }}
          disabled={quickLoading}
          onClick={() => quickPlay('headsup')}
          style={{
            backgroundImage: `linear-gradient(135deg, rgba(247,226,164,0.8), rgba(199,154,47,0.85)), url(/textures/gold-foil.jpg)`,
          }}
          className="rounded-2xl p-4 bg-cover bg-center border border-gold-400/30 text-left shadow-card"
        >
          <div className="text-ink-950 font-bold">Дуэль 1×1</div>
          <div className="text-ink-900/70 text-xs">Heads-up</div>
        </motion.button>
      </div>

      {/* Фильтры */}
      <div className="relative mb-3">
        <div ref={filterScrollRef} className="px-4 flex gap-2 overflow-x-auto scroll-x-touch">
          {(['all', 'sixmax', 'headsup'] as const).map((t) => (
            <FilterChip key={t} active={typeFilter === t} onClick={() => setTypeFilter(t)}>
              {t === 'all' ? 'Все' : t === 'sixmax' ? '6-max' : 'Heads-up'}
            </FilterChip>
          ))}
          <div className="w-px bg-white/10 mx-1 shrink-0" />
          {(['all', 'low', 'mid', 'high'] as const).map((b) => (
            <FilterChip key={b} active={blindFilter === b} onClick={() => setBlindFilter(b)}>
              {{ all: 'Любой блайнд', low: 'Низкий', mid: 'Средний', high: 'Высокий' }[b]}
            </FilterChip>
          ))}
        </div>
        {/* Лёгкое затухание справа — визуальная подсказка, что список скроллится дальше */}
        <div className="pointer-events-none absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-ink-950 to-transparent" />
      </div>

      <div className="px-4 space-y-2">
        {tables === null && !error && <LoadingState label="Загружаю столы…" />}
        {error && <ErrorState message={error} onRetry={load} />}
        {tables && filtered.length === 0 && !error && (
          <EmptyState title="Нет столов под фильтр" subtitle="Попробуйте изменить фильтры" icon="🂠" />
        )}
        {filtered.map((t) => (
          <button
            key={t.id}
            onClick={() => onOpenTable(t)}
            className="w-full flex items-center justify-between bg-ink-800/70 rounded-xl px-4 py-3 border border-white/5 hover:border-gold-500/30 transition-colors"
          >
            <div className="text-left">
              <div className="text-white font-semibold text-sm">{t.name}</div>
              <div className="text-white/40 text-xs flex items-center gap-1">
                Блайнды {t.smallBlind}/{t.bigBlind} · Бай-ин {t.minBuyIn}–{t.maxBuyIn}
                <ChipIcon size={11} className="ml-0.5" />
              </div>
            </div>
            <div className="text-right">
              <div className="text-emerald-400 text-sm font-semibold tabular-nums">
                {t.playersCount}/{t.maxSeats}
              </div>
              <div className="text-white/30 text-[10px]">{t.type === 'headsup' ? 'Дуэль' : '6-max'}</div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

function FilterChip({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      className={`whitespace-nowrap shrink-0 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
        active ? 'bg-gold-500/20 border-gold-500/50 text-gold-400' : 'bg-transparent border-white/10 text-white/50'
      }`}
    >
      {children}
    </button>
  );
}
